import { Box, type SxProps, type Theme } from "@mui/material";
import type { FC, ReactNode } from "react";
import { FONT_SIZE, INK, LINE_HEIGHT } from "../../../theme/tokens";

export type FactListItem = {
	/** Already translated. Also the row's key, so labels in one list are unique. */
	label: string;
	value: ReactNode;
	dataTestId?: string;
};

type Props = {
	items: readonly FactListItem[];
	dataTestId?: string;
	sx?: SxProps<Theme>;
};

/**
 * A short run of facts as a two-column description list: muted terms in a column sized to the longest, values beside
 * them in the primary ink. For the "Posted · Lead time · Census" block under an explanation, where each fact is one
 * line — `LabeledField` stacks a label over a value for a field grid instead.
 */
export const FactList: FC<Props> = ({ items, dataTestId, sx }) => (
	<Box
		component="dl"
		data-testid={dataTestId}
		sx={[
			{ display: "grid", gridTemplateColumns: "auto 1fr", columnGap: 1.75, rowGap: 0.75, m: 0, fontSize: FONT_SIZE.CAPTION, lineHeight: LINE_HEIGHT.SNUG },
			...(sx === undefined ? [] : Array.isArray(sx) ? sx : [sx]),
		]}
	>
		{items.map((item) => (
			<Box key={item.label} data-testid={item.dataTestId} sx={{ display: "contents" }}>
				<Box component="dt" sx={{ color: INK.MUTED }}>
					{item.label}
				</Box>
				<Box component="dd" sx={{ m: 0, color: INK.PRIMARY, fontWeight: 600, minWidth: 0 }}>
					{item.value}
				</Box>
			</Box>
		))}
	</Box>
);
